import { baseApi } from '@/api/core/baseApi';
import { unwrapApiResponse } from '@/api/patterns/crud';
import type { DashboardData, HealthDetailedResponse, ReadinessData } from '../types';
import { config } from '@/config';

export const monitoringApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getMonitoringDashboard: builder.query<DashboardData, void>({
      query: () => '/monitoring/dashboard',
      transformResponse: unwrapApiResponse<DashboardData>,
    }),

    // /health/detailed requires auth but is NOT under /api — queryFn with baseQuery
    // `as` cast: baseQuery returns { data: unknown } — framework interop boundary
    getHealthDetailed: builder.query<HealthDetailedResponse, void>({
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const backendOrigin = config.api.baseUrl.replace(/\/api$/, '');
        const result = await baseQuery(`${backendOrigin}/health/detailed`);
        if (result.error) return { error: result.error };
        return { data: result.data as HealthDetailedResponse };
      },
    }),

    // /health/ready is unauthenticated (k8s probe) — raw fetch required (no auth headers)
    // `as` cast: Response.json() returns unknown — framework interop boundary
    // No providesTags: read-only diagnostics with no corresponding mutations
    getHealthReady: builder.query<ReadinessData, void>({
      queryFn: async () => {
        try {
          const backendOrigin = config.api.baseUrl.replace(/\/api$/, '');
          const res = await fetch(`${backendOrigin}/health/ready`, { method: 'GET' });
          const data = (await res.json()) as ReadinessData;
          return { data };
        } catch (err: unknown) {
          return {
            error: {
              status: 'FETCH_ERROR' as const,
              error: err instanceof Error ? err.message : 'Health check failed',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetMonitoringDashboardQuery,
  useGetHealthDetailedQuery,
  useGetHealthReadyQuery,
} = monitoringApi;
